import React, { useState } from 'react';
import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  Grid,
  LinearProgress,
  Typography,
  Alert
} from '@mui/material';
import { CloudUpload } from '@mui/icons-material';
import axios from 'axios';
import { API_ENDPOINTS } from '../../../Api/endpoints';

const EmployeePdfImport = ({ open, onClose, onSuccess }) => {
  const [file, setFile] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [error, setError] = useState(null);

  const getAuthToken = () => localStorage.getItem('authToken');

  const handleFileChange = (event) => {
    const selectedFile = event.target.files[0];
    const isPdf = selectedFile && (selectedFile.type === 'application/pdf' || selectedFile.name?.toLowerCase().endsWith('.pdf'));

    if (selectedFile && !isPdf) {
      setError('Please select a PDF file');
      setFile(null);
      return;
    }

    setFile(selectedFile || null);
    setError(null);
  };

  const handleSubmit = async (event) => {
    event.preventDefault();

    if (!file) {
      setError('Please select a PDF file');
      return;
    }

    setUploading(true);
    setUploadProgress(0);
    setError(null);

    // Retry logic with 3 attempts
    const MAX_RETRIES = 3;
    const RETRY_DELAY = 2000;
    let lastError = null;

    for (let attempt = 1; attempt <= MAX_RETRIES; attempt++) {
      try {
        console.log(`Import attempt ${attempt} of ${MAX_RETRIES}`);
        
        const formData = new FormData();
        formData.append('file', file);

        const token = getAuthToken();
        const response = await axios.post(API_ENDPOINTS.EMPLOYEES.IMPORT_PDF, formData, {
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'multipart/form-data'
          },
          timeout: 60000,
          onUploadProgress: (progressEvent) => {
            const total = progressEvent.total || 1;
            const percentCompleted = Math.round((progressEvent.loaded * 100) / total);
            setUploadProgress(percentCompleted);
          }
        });

        // Check if response indicates an error (even with 200 status)
        if (response.data && response.data.code && response.data.code.includes('ERROR')) {
          throw new Error(response.data.message || 'Processing error');
        }

        // Success - return response
        onSuccess(response);
        setUploading(false);
        return;
      } catch (err) {
        lastError = err;
        console.warn(`Import attempt ${attempt} failed:`, err.message || err);

        if (attempt < MAX_RETRIES) {
          console.log(`Retrying in ${RETRY_DELAY}ms...`);
          await new Promise(res => setTimeout(res, RETRY_DELAY));
          setUploadProgress(0); // Reset progress for retry
        }
      }
    }

    // All retries failed
    console.error('Error importing employees pdf after 3 attempts:', lastError);
    if (lastError.response) {
      setError(`Import failed: ${lastError.response.data?.message || 'Unknown error'}`);
    } else {
      setError(`Import failed after 3 attempts: ${lastError.message || 'Please check your file and try again.'}`);
    }
    setUploading(false);
  };

  const handleClose = () => {
    if (!uploading) {
      setFile(null);
      setError(null);
      setUploadProgress(0);
      onClose();
    }
  };

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="md" fullWidth disableEscapeKeyDown={uploading}>
      <DialogTitle>Import Employees from PDF</DialogTitle>
      <DialogContent>
        <DialogContentText paragraph>
          Upload a PDF file to import employees.
        </DialogContentText>

        {error && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}

        <form onSubmit={handleSubmit}>
          <Grid container spacing={3}>
            <Grid item xs={12}>
              <Box
                border={1}
                borderRadius={1}
                borderColor="divider"
                p={3}
                textAlign="center"
                sx={{
                  borderStyle: 'dashed',
                  backgroundColor: 'background.paper',
                  '&:hover': {
                    backgroundColor: 'action.hover',
                    cursor: 'pointer'
                  }
                }}
                onClick={() => document.getElementById('pdf-file-input').click()}
              >
                <input
                  id="pdf-file-input"
                  type="file"
                  accept="application/pdf,.pdf"
                  onChange={handleFileChange}
                  style={{ display: 'none' }}
                  disabled={uploading}
                />
                <CloudUpload fontSize="large" color="primary" />
                <Typography variant="h6" mt={1}>
                  {file ? file.name : 'Click to select PDF file or drag and drop'}
                </Typography>
                <Typography variant="body2" color="textSecondary">
                  Only PDF files are accepted
                </Typography>
              </Box>
              <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                Import will process the uploaded PDF and create employees in bulk. If any records fail, you’ll see them in the response.
              </Typography>
            </Grid>

            {uploading && (
              <Grid item xs={12}>
                <Box mt={2}>
                  <Typography variant="body2" gutterBottom>
                    Uploading and processing: {uploadProgress}%
                  </Typography>
                  <LinearProgress variant="determinate" value={uploadProgress} />
                </Box>
              </Grid>
            )}
          </Grid>
        </form>
      </DialogContent>
      <DialogActions>
        <Button onClick={handleClose} disabled={uploading}>
          Cancel
        </Button>
        <Button onClick={handleSubmit} variant="contained" color="primary" disabled={!file || uploading}>
          Import
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default EmployeePdfImport;
